import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CopyrightIcon from '@mui/icons-material/Copyright';
import PrintIcon from '@mui/icons-material/Print';
import Info from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import generateSalePDF from '../sale/Bill';
import '../shared.css';

function SaleIndex() {
  const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
  const history = useHistory();
  const [data, setData]                     = useState([]);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showSearch, setShowSearch]         = useState(false);
  const [deleteItemId, setDeleteItemId]     = useState(null);
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [fromDate, setFromDate]             = useState('');
  const [toDate, setToDate]                 = useState('');

  const hasCreate = permissions.includes('Create Transaction');
  const hasEdit   = permissions.includes('Edit Transaction');
  const hasDelete = permissions.includes('Delete Transaction');

  const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
  const getFormattedStartDate   = () => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    setFromDate(getFormattedStartDate());
    setToDate(getFormattedCurrentDate());
    fetchTableData();
  }, [isAuthenticated, companyId, branchId, token, logout]);

  const fetchTableData = async () => {
    setData([]);
    try {
      const response = await fetchData('SaleApi', `/CustomerDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      if (response.responseCode === '1111') setData(response.response_data);
    } catch (error) {
      console.error('Error fetching Sale data:', error);
      if (error.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    }
  };

  const handleDelete = () => {
    deleteData('SaleApi', `${deleteItemId}`, token)
      .then(() => { fetchTableData(); setDeleteItemId(null); setConfirmOpen(false); })
      .catch(err => console.error(err));
  };

  const handleActivate = (id) => {
    activateData('SaleApi', `${id}`, token).then(fetchTableData).catch(console.error);
  };

  const handlePrint = async (saleId) => {
    try {
      const response = await fetchData('SaleApi', `/SalePrint/${saleId}/${branchId}`, token);
      generateSalePDF(response.response_data);
    } catch (error) {
      console.error('Error printing Invoice:', error);
    }
  };

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  const filtered = data.filter(item =>
    Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="pg-container">
      {/* ── Header ── */}
      <div className="pg-header">
        <h5 className="pg-title">Sale</h5>
        <div className="pg-header-actions">
          {/* Date filters */}
          <div className="pg-date-row">
            <div className="pg-field">
              <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
              <input className="pg-input" type="date" value={fromDate}
                onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="pg-field">
              <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
              <input className="pg-input" type="date" value={toDate}
                onChange={e => setToDate(e.target.value)} />
            </div>
            <button className="pg-btn pg-btn-outline" onClick={fetchTableData} title="Refresh">
              <RefreshIcon /> Refresh
            </button>
          </div>

          <div className="pg-action-row">
            <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
              <SearchIcon /> Search
            </button>
            {hasCreate && (
              <Link to="/SaleCreate" className="pg-btn pg-btn-primary">
                <AddIcon /> Create New
              </Link>
            )}
          </div>
          {showSearch && (
            <input className="pg-input pg-search-input" placeholder="Search…"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="pg-table-wrap">
        <table className="pg-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Bill Date</th>
              <th>Bill Number</th>
              <th>Customer</th>
              <th>Phone No</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ textAlign: 'right' }}>CGST</th>
              <th style={{ textAlign: 'right' }}>SGST</th>
              <th style={{ textAlign: 'right' }}>IGST</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={11} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
              : filtered.map((item, i) => (
                <tr key={item.Id} style={{ opacity: item.IsActive ? 1 : 0.5 }}>
                  <td>{i + 1}</td>
                  <td>{formatDate(item.BillDate)}</td>
                  <td>{item.BillPrefix}/{item.BillNumber}/{item.FinYear}</td>
                  <td>{item.CustomerName}</td>
                  <td>{item.ContactNumber}</td>
                  <td style={{ textAlign: 'right' }}>{item.TaxableAmount ? item.TaxableAmount.toFixed(2) : '0.00'}</td>
                  <td style={{ textAlign: 'right' }}>{item.CGSTAmount ? item.CGSTAmount.toFixed(2) : '0.00'}</td>
                  <td style={{ textAlign: 'right' }}>{item.SGSTAmount ? item.SGSTAmount.toFixed(2) : '0.00'}</td>
                  <td style={{ textAlign: 'right' }}>{item.IGSTAmount ? item.IGSTAmount.toFixed(2) : '0.00'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{item.GrandTotal ? item.GrandTotal.toFixed(2) : '0.00'}</td>
                  <td className="actions-cell">
                    <Link to={`/SaleDetails/${item.Id}`}>
                      <IconButton size="small" title="Details"><Info style={{ color: 'coral', fontSize: 17 }} /></IconButton>
                    </Link>
                    <IconButton size="small" onClick={() => handlePrint(item.Id)} title="Print">
                      <PrintIcon style={{ color: 'darkgoldenrod', fontSize: 17 }} />
                    </IconButton>
                    {hasEdit && item.IsActive && (
                      <Link to={`/SaleEdit/${item.Id}`}>
                        <IconButton size="small" color="primary" title="Edit"><EditIcon style={{ fontSize: 17 }} /></IconButton>
                      </Link>
                    )}
                    {hasDelete && (
                      <IconButton size="small" title={item.IsActive ? 'Delete' : 'Activate'}
                        onClick={() => item.IsActive ? (setDeleteItemId(item.Id), setConfirmOpen(true)) : handleActivate(item.Id)}>
                        {item.IsActive
                          ? <DeleteIcon style={{ fontSize: 17, color: '#dc2626' }} />
                          : <ActivateIcon style={{ fontSize: 17, color: 'darkolivegreen' }} />}
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Record?</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
        <DialogActions>
          <button className="pg-btn pg-btn-danger" onClick={() => setConfirmOpen(false)}>Cancel</button>
          <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
        </DialogActions>
      </Dialog>

      <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
    </div>
  );
}

export default SaleIndex;
